import { useState, useCallback, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PromoCode, PromoCodeInput } from './types';
import { toast } from '@/components/ui/use-toast';
import { User } from '@/context/auth/types';

export const usePromoCodesManager = (
  user: User | null,
  selectedRestaurants: string[]
) => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Memoize selectedRestaurants to prevent infinite re-renders
  const stableSelectedRestaurants = useMemo(() => {
    return selectedRestaurants.slice().sort(); // Create a stable, sorted copy
  }, [selectedRestaurants.join(',')]); // Use join as dependency to compare array contents

  // Fetch promo codes
  const fetchPromoCodes = useCallback(async () => {
    if (!user) {
      setPromoCodes([]);
      setLoading(false);
      setError(null);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Determine which restaurants to query
      const restaurantIds = stableSelectedRestaurants.includes('all') 
        ? user.restaurants?.map(r => r.id) || []
        : stableSelectedRestaurants;
      
      if (restaurantIds.length === 0 || stableSelectedRestaurants.length === 0) {
        setPromoCodes([]);
        setLoading(false);
        setError(null);
        return;
      }
      
      const { data, error: fetchError } = await supabase
        .from('promo_codes')
        .select('*')
        .in('restaurant_id', restaurantIds)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      setPromoCodes(data || []);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch promo codes';
      setError(errorMessage);
      
      // Only show toast for actual errors, not for empty results
      if (err instanceof Error && !err.message.includes('relation "promo_codes" does not exist')) {
        toast({
          title: "Error",
          description: "Failed to load promo codes. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user, stableSelectedRestaurants]);

  // Create promo code
  const createPromoCode = async (codeData: PromoCodeInput): Promise<PromoCode | null> => {
    if (!user) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive"
      });
      return null;
    }

    try {
      setIsCreating(true);
      
      const payload = {
        ...codeData,
        created_by: user.id,
        organisation_id: user.organisationId,
        current_usage: 0,
        active: codeData.active !== undefined ? codeData.active : true
      };

      const { data, error: createError } = await supabase
        .from('promo_codes')
        .insert([payload])
        .select()
        .single();

      if (createError) throw createError;

      // Refresh the list
      await fetchPromoCodes();
      
      toast({
        title: "Success",
        description: "Promo code created successfully"
      });

      return data;
    } catch (err) {
      console.error('Error creating promo code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create promo code';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  // Update promo code
  const updatePromoCode = async (id: number, updates: Partial<PromoCode>): Promise<boolean> => {
    try {
      setIsUpdating(true);
      
      const { error: updateError } = await supabase
        .from('promo_codes')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      // Refresh the list
      await fetchPromoCodes();
      
      toast({
        title: "Success",
        description: "Promo code updated successfully"
      });

      return true;
    } catch (err) {
      console.error('Error updating promo code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update promo code';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete promo code
  const deletePromoCode = async (id: number): Promise<boolean> => {
    try {
      setIsDeleting(true);
      
      const { error: deleteError } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Refresh the list
      await fetchPromoCodes();
      
      toast({
        title: "Success",
        description: "Promo code deleted successfully"
      });

      return true;
    } catch (err) {
      console.error('Error deleting promo code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete promo code';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  // Load data when dependencies change
  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  return {
    promoCodes,
    loading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    refreshPromoCodes: fetchPromoCodes
  };
};